import { Badge, Chip } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';

export type TMplChipVariant =
	| 'default'
	| 'primary'
	| 'secondary'
	| 'error'
	| 'warning'
	| 'success'
	| 'info';

export interface IMplChipStyledProps {
	chipVariant: TMplChipVariant;
	bgColor?: string;
	textColor?: string;
	circle?: boolean;
	elevated?: boolean;
	hasLabel?: boolean;
}

/** Default background/text color pairs for each preset variant. */
const getVariantColors = (
	theme: Theme,
	variant: TMplChipVariant
): { bg: string; color: string } => {
	switch (variant) {
		case 'primary':
			return {
				bg: theme.palette.primary.main,
				color: theme.palette.neutral[0],
			};
		case 'secondary':
			return {
				bg: theme.palette.secondary.main,
				color: theme.palette.primary.dark,
			};
		case 'error':
			return { bg: theme.palette.error.main, color: theme.palette.neutral[0] };
		case 'warning':
			return {
				bg: theme.palette.warning.main,
				color: theme.palette.neutral[0],
			};
		case 'success':
			return {
				bg: theme.palette.bewegt?.[800] ?? '#267E3B',
				color: theme.palette.neutral[0],
			};
		case 'info':
			return {
				bg: theme.palette.solutions?.[100] ?? '#DCDFE5',
				color: theme.palette.solutions?.[800] ?? '#454E5E',
			};
		case 'default':
		default:
			return {
				bg: theme.palette.neutral[0],
				color: theme.palette.primary.main,
			};
	}
};

export const MplChipStyled = styled(Chip)<{ ownerState: IMplChipStyledProps }>(
	({ theme, ownerState }) => {
		const { chipVariant, bgColor, textColor, circle, elevated, hasLabel } =
			ownerState;
		const variantColors = getVariantColors(theme, chipVariant);
		const background = bgColor ?? variantColors.bg;
		const color = textColor ?? variantColors.color;

		return {
			height: 'auto',
			padding: hasLabel ? '4px 8px' : 4,
			display: 'flex',
			alignItems: 'center',
			gap: 4,
			backgroundColor: background,
			color,
			border:
				chipVariant === 'default'
					? `0.25px solid ${theme.palette.primary[200]}`
					: 'none',
			...(elevated && {
				boxShadow: '0 4px 6px -4px rgba(24, 39, 75, 0.12)',
			}),
			...(circle && {
				width: 32,
				height: 32,
				padding: 0,
				borderRadius: '50%',
				justifyContent: 'center',
			}),

			'& .MuiChip-label': {
				...theme.typography.l1,
				padding: 0,
				...(!hasLabel && { display: 'none' }),
			},

			'& .MuiChip-icon': {
				margin: 0,
				color: 'inherit',
			},

			'&& .MuiChip-deleteIcon': {
				margin: 0,
				color: 'inherit',
				fontSize: 16,
			},

			'&, &.MuiChip-clickable:hover': {
				backgroundColor: background,
			},
		};
	}
);

export const MplChipBadgeStyled = styled(Badge)(({ theme }) => ({
	'& .MuiBadge-badge': {
		top: 3,
		right: 3,
		minWidth: 16,
		width: 16,
		height: 16,
		padding: 0,
		borderRadius: '50%',
		...theme.typography.l2,
	},
}));
